import { Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import { useEffect } from "react"
import MergeAddNewLoan from "./merge-add-new-loan"
import MergeLoansPaydownInfoMessage from "./merge-loans-paydown-info-message"
import { ReactComponent as PlusIcon } from 'app/styles/add-circle-sharp.svg'
import { ReactComponent as ChevronDownIcon } from 'app/styles/chevronDown.svg'
import { LoanParams } from "app/models/loan-params"
import { FacilityParams } from "app/models/facility-params"
import { LoanMergeParams } from "app/models/loan-merge-params"
import { StableKey } from "app/models/common-types"
import { useDispatch, useSelector } from "react-redux"
import { saveLoanMerge } from "app/state/ducks/loan-merges/thunks"
import { SuccessNotification, ErrorNotification } from "app/views/components/notifications/notification"
import { formatDateToUTC, stringToDate } from "app/utils/util-functions"
import { getFacilities, getFacility } from "app/state/ducks/facilities/selectors"
import { arePikFieldsApplicable } from '../loans/utilities/pik-config-utils'

type NewLoan = {
    indexRateOption: string
    amount: string
    borrower: string
    startDate: Date | null
    maturityDate: Date | null
    interestBaseRate: string
    interestBaseRateRounding: string
    dayBasis: string
    margin: string
    cas: string
    allInRate: string
    facilityId: string
    currency: string
    // Rate to the facility base currency; '' when they match
    fxRate: string
    isNonProRata: boolean
    rounding: string
    pikPremium: string
    pikInterestPercent: string
    cashInterestPercent: string
    newLoanWithPik: string
}

type Params = {
    selectedLoans?: LoanParams[]
    closeModal: () => void
    existingMergeData?: LoanMergeParams
    /** The loans' facility as the modal that opened this step read it; the store is the fallback. */
    facility?: FacilityParams
}

export default function TermIndexRateForm({
    facility: givenFacility,
    selectedLoans,
    closeModal,
    existingMergeData
}: Params) {
    const dispatch = useDispatch()
    const allFacilities = useSelector(getFacilities)
    const loadedFacility = useSelector(getFacility)

    // The facility of the selected loans (all from the same facility): from
    // the facilities list when the page holds it, else the one the modal
    // fetched into the single-facility slot (the Servicing page holds no list).
    const firstLoanFacilityId = selectedLoans?.[0]?.facilityId
    const facilityId =
        typeof firstLoanFacilityId === 'object' ? firstLoanFacilityId?.id : firstLoanFacilityId
    const facility =
        givenFacility ??
        allFacilities?.find((f: any) => f.id === facilityId) ??
        (loadedFacility?.id === facilityId ? loadedFacility : undefined)

    // Helper function to create empty loan object with auto-populated backend fields
    const createEmptyLoan = (): NewLoan => {
        // Get auto-populated values from the first selected loan
        const firstLoan = selectedLoans?.[0]

        // Use the maturity date of the existing loans as the start date for new loans
        const startDate = firstLoan?.endDate ? stringToDate(firstLoan.endDate) : null

        return {
            indexRateOption: '',
            amount: '',
            borrower: '',
            startDate: startDate,
            maturityDate: null,
            interestBaseRate: '',
            interestBaseRateRounding: '',
            dayBasis: '',
            margin: '',
            cas: '',
            allInRate: '',
            facilityId: typeof firstLoan?.facilityId === 'object' ? firstLoan.facilityId.id : firstLoan?.facilityId || '',
            currency: firstLoan?.currency || '',
            fxRate: firstLoan?.fxRate?.toString() || '',
            isNonProRata: false,
            rounding: firstLoan?.rounding || '',
            pikPremium: '0',
            pikInterestPercent: '0',
            cashInterestPercent: '0',
            newLoanWithPik: '',
        }
    }

    // Helper function to convert a single LoanParams to NewLoan format
    const convertLoanParamsToNewLoan = (existingLoan: LoanParams): NewLoan => {
        let indexOptionValue = ''

        if (existingLoan.indexOption) {
            if (typeof existingLoan.indexOption === 'object' && existingLoan.indexOption !== null) {
                // Object format: { id: "...", admin: "..." }
                indexOptionValue = existingLoan.indexOption.id || ''
            } else if (typeof existingLoan.indexOption === 'string') {
                // String format: just the ID
                indexOptionValue = existingLoan.indexOption
            }
        }

        const convertedLoan = {
            indexRateOption: indexOptionValue,
            amount: existingLoan.amount?.toString() || '',
            borrower: typeof existingLoan.borrower === 'object' ? existingLoan.borrower.id : existingLoan.borrower || '',
            startDate: existingLoan.startDate ? stringToDate(existingLoan.startDate) : (selectedLoans?.[0]?.endDate ? stringToDate(selectedLoans[0].endDate) : null),
            maturityDate: existingLoan.endDate ? stringToDate(existingLoan.endDate) : null,
            interestBaseRate: existingLoan.interestBaseRate?.toString() || '',
            interestBaseRateRounding: existingLoan.interestBaseRateWithRounding?.toString() || '',
            dayBasis: existingLoan.dayBasis ? existingLoan.dayBasis.replace('_', '/') : '', // Convert back to UI format
            margin: existingLoan.margin?.toString() || '',
            cas: existingLoan.casValue?.toString() || '',
            allInRate: existingLoan.allInRate?.toString() || '',
            facilityId: typeof existingLoan.facilityId === 'object' ? existingLoan.facilityId.id : existingLoan.facilityId || '',
            currency: existingLoan.currency || '',
            fxRate: existingLoan.fxRate?.toString() || '',
            isNonProRata: existingLoan.isNonProRata ?? false,
            rounding: existingLoan.rounding || '',
            pikPremium: existingLoan.pikPremium?.toString() || '0',
            pikInterestPercent: existingLoan.pikInterestPercent?.toString() || '0',
            cashInterestPercent: existingLoan.cashInterestPercent?.toString() || '0',
            newLoanWithPik: '',
        }

        return convertedLoan
    }

    // Helper function to convert existing LoanMergeParams to form values
    const convertExistingDataToFormValues = (): { mergeType: string, newLoans: NewLoan[] } => {
        if (!existingMergeData || !existingMergeData.newLoans || existingMergeData.newLoans.length === 0) {
            return {
                mergeType: 'single',
                newLoans: [createEmptyLoan()]
            }
        }

        // Determine merge type based on number of new loans
        const mergeType = existingMergeData.newLoans.length > 1 ? 'multiple' : 'single'

        // Convert all new loans from existing merge data to form format
        const convertedLoans = existingMergeData.newLoans.map((existingLoan) => {
            return convertLoanParamsToNewLoan(existingLoan)
        })

        return {
            mergeType,
            newLoans: convertedLoans
        }
    }

    const form = useForm({
        initialValues: convertExistingDataToFormValues(),
        validate: {
            newLoans: {
                newLoanWithPik: (value: string) => {
                    if (facility?.pikOption === 'PIK_Optional' && !value) {
                        return 'PIK (ON/OFF) selection is required for PIK Optional facilities'
                    }
                    return null
                },
            }
        }
    })

    // Reset form when selectedLoans changes or existingMergeData changes
    useEffect(() => {
        const formValues = convertExistingDataToFormValues()
        form.setValues(formValues)
    }, [selectedLoans, existingMergeData])

    const mergeTypeOptions = [
        { value: 'single', label: 'Create Single New Loan' },
        { value: 'multiple', label: 'Split Into Multiple New Loans' }
    ]

    // Update loans array based on merge type
    const handleMergeTypeChange = (value: string) => {
        const currentValue = form.values.mergeType
        form.setFieldValue('mergeType', value)

        // Only reset loans if we're changing to a different merge type
        // This preserves existing data when editing
        if (value !== currentValue) {
            if (value === 'single') {
                // When switching to single, keep the first loan if it exists, otherwise create empty
                const existingLoans = form.values.newLoans
                const firstLoan = existingLoans.length > 0 ? existingLoans[0] : createEmptyLoan()
                form.setFieldValue('newLoans', [firstLoan])
            } else if (value === 'multiple') {
                // When switching to multiple, ensure we have at least 2 loans
                const existingLoans = form.values.newLoans
                if (existingLoans.length < 2) {
                    const newLoans = [...existingLoans]
                    while (newLoans.length < 2) {
                        newLoans.push(createEmptyLoan())
                    }
                    form.setFieldValue('newLoans', newLoans)
                }
            }
        }

        form.clearErrors()
    }

    const handleAmountChange = (index: number, value: string) => {
        form.setFieldValue(`newLoans.${index}.amount`, value)
    }

    // Handlers for more loan options panel fields
    const handleFieldChange = (index: number, field: keyof NewLoan, value: string | Date | null) => {
        form.setFieldValue(`newLoans.${index}.${field}`, value)
    }

    // Transform form data to match backend LoanMergeParams structure
    const transformFormDataToLoanMergeParams = (values: typeof form.values): LoanMergeParams => {
        // Get source loan IDs as StableKey array
        const sourceLoans: StableKey[] = selectedLoans?.map(loan => ({
            id: loan.id!,
            admin: loan.agencyAdmin
        })).filter(loan => loan.id) || []

        // Get effective date from source loans (should be same for all)
        const effectiveDate = selectedLoans?.[0]?.endDate || formatDateToUTC(new Date())

        // Transform NewLoan objects to LoanParams objects
        const newLoans: LoanParams[] = values.newLoans.map(newLoan => ({
            facilityId: {
                id: newLoan.facilityId,
                admin: selectedLoans?.[0]?.agencyAdmin || ''
            },
            amount: parseFloat(newLoan.amount) || 0,
            currency: newLoan.currency,
            dayBasis: (newLoan.dayBasis != '' ? newLoan.dayBasis.replace('/', '_') : null) as any,
            interestBaseRate: parseFloat(newLoan.interestBaseRate) || null,
            interestBaseRateWithRounding: parseFloat(newLoan.interestBaseRateRounding.replace('%', '')) || null,
            rounding: (newLoan.rounding != '' ? newLoan.rounding : null) as any,
            endDate: newLoan.maturityDate ? formatDateToUTC(newLoan.maturityDate) : effectiveDate,
            startDate: newLoan.startDate ? formatDateToUTC(newLoan.startDate) : formatDateToUTC(new Date()),
            isSwingLine: false,
            isNonProRata: newLoan.isNonProRata,
            margin: parseFloat(newLoan.margin) || 0,
            allInRate: (() => {
                // Front end displays all in rate with PIK premium included for convenience,
                // Back end expects all in rate without PIK premium, so we need to subtract it
                const displayedAllInRate =parseFloat(newLoan.allInRate.replace('%', '')) || 0
                const pikPremium = parseFloat(newLoan.pikPremium) || 0
                return displayedAllInRate - pikPremium
            })(),
            indexOption: {
                id: newLoan.indexRateOption,
                admin: selectedLoans?.[0]?.agencyAdmin || ''
            },
            borrowerPaymentInstructions: null,
            lendersPositions: [],
            contractPeriod: null,
            casValue: parseFloat(newLoan.cas) || 0,
            fxRate: parseFloat(newLoan.fxRate) || null,
            agencyAdmin: selectedLoans?.[0]?.agencyAdmin || '',
            agencyManager: selectedLoans?.[0]?.agencyManager || '',
            borrower: {
                id: newLoan.borrower,
                admin: selectedLoans?.[0]?.agencyAdmin || ''
            },
            accrualInterestMaturityDate: 0,
            nextInterestPaymentDate: null,
            nextPIKDate: null,
            pikPremium: (() => {
                if (!arePikFieldsApplicable(facility?.pikOption, newLoan.newLoanWithPik === 'Yes')) return null
                const parsed = parseFloat(newLoan.pikPremium)
                return isNaN(parsed) ? null : parsed
            })(),
            pikInterestPercent: (() => {
                if (!arePikFieldsApplicable(facility?.pikOption, newLoan.newLoanWithPik === 'Yes')) return null
                const parsed = parseFloat(newLoan.pikInterestPercent)
                return isNaN(parsed) ? null : parsed
            })(),
            cashInterestPercent: (() => {
                if (!arePikFieldsApplicable(facility?.pikOption, newLoan.newLoanWithPik === 'Yes')) return null
                const parsed = parseFloat(newLoan.cashInterestPercent)
                return isNaN(parsed) ? null : parsed
            })(),
            allInRateWithPikPremium: (() => {
                // displayed all in rate already includes PIK premium for user convenience, so we just return it
                const displayedAllInRate = parseFloat(newLoan.allInRate.replace('%', '')) || 0
                return displayedAllInRate
            })(),
            swinglineLenders: [],
            pikPremiumInterestAtMaturity: 0,
            cashInterestAmountAtMaturity: 0,
            pikInterestAmountAtMaturity: 0

        }))

        return {
            id: existingMergeData?.id, // Include existing ID for updates
            sourceLoans,
            newLoans,
            effectiveDate,
            status: 'Draft',
            hasError: false,
            ErrorMessage: '',
            hasSuccessfulEntitySave: false,
            customer_token: '',
        }
    }

    const handleSubmit = async (values: typeof form.values) => {
        try {
            // Transform form data to backend format
            const loanMergeParams = transformFormDataToLoanMergeParams(values)

            const result = await dispatch(saveLoanMerge(loanMergeParams, existingMergeData) as any)

            if (result.success) {
                const isEditing = !!existingMergeData?.id
                SuccessNotification({
                    title: isEditing ? 'Draft Loan Merge Updated' : 'Draft Loan Merge Created',
                    message: isEditing
                        ? 'The draft loan merge has been successfully updated.'
                        : 'The draft loan merge has been successfully saved.',
                })
                closeModal()
            } else {
                ErrorNotification({
                    title: 'Save Failed',
                    message: result.payload || 'Failed to save the draft loan merge. Please try again.',
                })
            }
        } catch (error) {
            console.error('Error submitting draft loan merge:', error)
            ErrorNotification({
                title: 'Save Error',
                message: 'An unexpected error occurred while saving the draft loan merge. Please try again.',
            })
        }
    }

    const handleAddLoan = () => {
        const currentLoans = form.values.newLoans
        form.setFieldValue('newLoans', [...currentLoans, createEmptyLoan()])
    }

    const handleRemoveLoan = (index: number) => {
        const currentLoans = form.values.newLoans
        if (currentLoans.length > 1) {
            const updatedLoans = currentLoans.filter((_, i) => i !== index)
            form.setFieldValue('newLoans', updatedLoans)
        }
    }

    const renderNewLoans = () => {
        return form.values.newLoans.map((loan, index) => (
            <MergeAddNewLoan
                key={index}
                index={index}
                loan={loan}
                mergeType={form.values.mergeType}
                selectedLoans={selectedLoans}
                onIndexRateOptionChange={(idx, value) => form.setFieldValue(`newLoans.${idx}.indexRateOption`, value)}
                onAmountChange={handleAmountChange}
                onFieldChange={handleFieldChange}
                onRemove={handleRemoveLoan}
                showRemoveButton={form.values.newLoans.length > 1}
            />
        ))
    }

    return (
        <form className="merge-loans-form" onSubmit={form.onSubmit(handleSubmit)}>

            <BlackSelectInput
                label="Merge Type"
                data={mergeTypeOptions}
                value={form.values.mergeType}
                onChange={handleMergeTypeChange}
                disabled={!!existingMergeData} // Disable when editing existing merge
                classNames={{
                    label: "black-input-label-with-padding",
                    input: existingMergeData ? "" : "merge-form-editable-select" // Apply disabled styling when editing
                }}
                rightSection={<ChevronDownIcon />}
            />

            {form.values.mergeType && (
                <div className="merge-loans-new-loans-container">
                    {renderNewLoans()}

                    {form.values.mergeType === 'multiple' && (
                        <div className="add-loan-action" onClick={handleAddLoan}>
                            <PlusIcon />
                            <Text className="add-loan-action-text">Add Loan</Text>
                        </div>
                    )}
                </div>
            )}

            {/* Paydown info section for multiple merge type */}
            {form.values.mergeType === 'multiple' && (

                <div className="merge-loans-paydown-section">
                    <MergeLoansPaydownInfoMessage
                        selectedLoans={selectedLoans}
                        newLoanAmounts={form.values.newLoans.map(loan => loan.amount)}
                        isMultipleMode={true}
                    />
                </div>

            )}


            <div className="actions-buttons-block">
                <button
                    type="button"
                    className="merge-loan-cancel-button"
                    onClick={() => closeModal()}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="merge-loan-save-button"
                >
                    Save
                </button>
            </div>

        </form>
    )
}